
import React from "react";
import { useSearchParams } from "@rivtn/noseur";
import { i18nManager } from "../i18n";
import NewsCard from "./common/NewsCard";
import PageHead from "./common/PageHead";
import Pagination from "./common/Pagination";
import FilterChips from "./common/FilterChips";
import { Utils } from "../../../../utils";
import styles from "../assets/style.module.css";
import { ApiResponseData, BaseService, ITeamNews, ITeamWebsite, OmneHostService, SanitizedResponse } from "../../../../services";

function NewsDetail({ article, onBack }: { article: ITeamNews; onBack: () => void; }) {

    const labels = i18nManager.Labels.news;

    return (<>
        <section className={styles["page-head"]}>
            <div className={styles["container-narrow"]}>
                <button className={styles["back-btn"]} onClick={onBack}><i className="fa fa-arrow-left" /> {labels.back}</button>
                {article.extra_data?.category && (<div className={styles["tag"]}>{article.extra_data.category}</div>)}
                <h1 className={styles["jumbo"]}>{article.title}</h1>
                <span className={styles["news-card-date"]}>{Utils.fineDate(new Date(article.updated_at))}</span>
            </div>
        </section>
        <section className={styles["section-page"]}>
            <div className={styles["container-narrow"]}>
                {article.extra_data?.featured_image && (<img className={styles["article-img"]} src={article.extra_data.featured_image} alt={article.title} />)}
                <div className={styles["article-body"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(article.content) }} />
            </div>
        </section>
    </>);
}

export interface NewsProps {
    website: ITeamWebsite;
}

function News({ website }: NewsProps) {

    const labels = i18nManager.Labels.news;
    const omnehostService = OmneHostService.getInstance();
    const [category, setCategory] = React.useState("");
    const [teamNews, setTeamNews] = React.useState<ApiResponseData<ITeamNews>>();
    const [selectedNews, setSelectedNews] = React.useState<ITeamNews | null>(null);
    const { page, setPage, article, setArticle } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", article: "" } });
    const categories = Array.from(new Set((teamNews?.content ?? []).map((n) => n.extra_data?.category).filter(Boolean) as string[]));

    React.useEffect(fetchInformation, [omnehostService, page]);
    React.useEffect(() => {
        if (!article) {
            setSelectedNews(null);
            return;
        }
        const found = teamNews?.content?.find((n) => n.external_id === article);
        if (found) setSelectedNews(found);
    }, [article, teamNews]);
    React.useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [selectedNews]);

    if (selectedNews) {
        return (<NewsDetail article={selectedNews} onBack={() => {
            setSelectedNews(null);
            setArticle(undefined);
        }} />);
    }

    return (<>
        <PageHead kicker={labels.kicker} title={labels.title} subtitle={labels.desc} />
        <section className={styles["section-page"]}>
            <div className={styles["container"]}>
                {categories.length > 0 && (<FilterChips value={category} onChange={setCategory} options={[["", labels.all], ...categories.map((c) => [c, c] as [string, string])]} />)}
                {(!!teamNews?.content)
                    ? (teamNews.content.length > 0
                        ? (<div className={styles["news-grid"]}>
                            {teamNews.content.filter((n) => !category || n.extra_data?.category === category).map((n) => (
                                <NewsCard key={n.external_id} news={n} onClick={() => setArticle(n.external_id)} />
                            ))}
                        </div>)
                        : Utils.emptyState())
                    : Utils.loadingState()}
                <Pagination page={parseInt(`${page}`)} rowsPerPage={teamNews?.size} totalRecords={teamNews?.total_elements} onPageChange={(p) => setPage(`${p}`)} />
            </div>
        </section>
    </>);

    function fetchInformation() {
        setTeamNews({});
        omnehostService.queryTeamNews(website.team.public_id, {
            page,
            sort: "updated_at,DESC",
        }).then(({ apiResponse }: SanitizedResponse<ITeamNews>) => {
            setTeamNews(apiResponse.data!);
        }).catch(BaseService.reportError);
    }
}

export default News;
